import { useRef, type KeyboardEvent } from 'react';
import './tabs.css';

export interface TabItem<TKey extends string = string> {
  key: TKey;
  label: string;
}

interface TabsProps<TKey extends string = string> {
  items: readonly TabItem<TKey>[];
  activeKey: TKey;
  onChange: (key: TKey) => void;
  /** Names the tablist for assistive technology. */
  label: string;
  /** Id prefix so each tab can point at its panel. */
  idPrefix?: string;
  className?: string;
}

export function Tabs<TKey extends string = string>({
  items,
  activeKey,
  onChange,
  label,
  idPrefix = 'tab',
  className = '',
}: TabsProps<TKey>) {
  const listRef = useRef<HTMLDivElement>(null);

  // Roving focus: arrows move between tabs, Home/End jump to the ends.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const offsets: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1 };
    const offset = offsets[event.key];
    const currentIndex = items.findIndex(item => item.key === activeKey);
    let nextIndex = -1;

    if (offset !== undefined) {
      nextIndex = (currentIndex + offset + items.length) % items.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = items.length - 1;
    }

    if (nextIndex < 0) return;
    event.preventDefault();
    const next = items[nextIndex];
    onChange(next.key);
    listRef.current
      ?.querySelector<HTMLButtonElement>(`#${idPrefix}-${next.key}`)
      ?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      className={`ui-tabs ${className}`.trim()}
      onKeyDown={handleKeyDown}
    >
      {items.map(item => {
        const isActive = item.key === activeKey;
        return (
          <button
            key={item.key}
            id={`${idPrefix}-${item.key}`}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`${idPrefix}-panel-${item.key}`}
            tabIndex={isActive ? 0 : -1}
            className={`ui-tabs__tab${isActive ? ' ui-tabs__tab--active' : ''}`}
            onClick={() => onChange(item.key)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
